/*
   Cache invalidation driven by the live socket.

   Split from `use-review-live.ts`, which owns the *transport-facing* view of a
   review — who is present, who is typing, where their cursors are. These two
   hooks own the opposite side: what a frame means for data already in React
   Query. Keeping them apart is what stops one file holding both a socket
   reducer and a cache policy.
*/

import { useQueryClient, type QueryClient } from "@tanstack/react-query"
import * as React from "react"

import { qk } from "@/lib/query-keys"
import { connectReview } from "@/lib/ws"

import type { ReviewId } from "../data/review-data"

import type { ConversationData } from "./use-review-comments"

const INVALIDATION_DEBOUNCE_MS = 250

/**
 * Keeps the messages panel current from the live socket.
 *
 * Separate from `useLiveInvalidation` because the two refresh different
 * caches: that one refetches the review detail, whose adapter sets
 * `messages: []` (see `adapter.ts`), so it has never carried the
 * conversation and cannot be made to by adding a frame type to it.
 *
 * A frame for a message this client already holds is skipped. The sender's
 * own message is put in cache optimistically and reconciled from the 201
 * body, so refetching on its echo would spend a round trip to arrive at the
 * state already on screen. The test is cache membership rather than
 * authorship, because the same person with two tabs open is two clients: the
 * other tab holds nothing and must still refetch.
 */
export function useLiveConversation(reviewId: ReviewId | null): void {
  const qc = useQueryClient()

  React.useEffect(() => {
    if (!reviewId) return
    const connection = connectReview(reviewId)
    let timer: ReturnType<typeof setTimeout> | null = null

    const off = connection.onFrame((frame) => {
      if (frame.type !== "comment.created") return
      const comment = frame.comment
      if (holdsComment(qc, reviewId, comment)) return
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        timer = null
        // Checked again here, not only when the frame arrived. The echo usually
        // beats the POST's own 201 — one hop against a round trip — so the
        // message can land in cache during the debounce, and refetching then
        // would spend a request to arrive at the state already on screen.
        if (holdsComment(qc, reviewId, comment)) return
        void qc.invalidateQueries({ queryKey: qk.reviews.comments(reviewId) })
      }, INVALIDATION_DEBOUNCE_MS)
    })

    return () => {
      if (timer) clearTimeout(timer)
      off()
      connection.close()
    }
  }, [qc, reviewId])
}

/**
 * Is this comment already in the conversation cache?
 *
 * The payload is `unknown` by declaration: it crosses a process boundary and a
 * bus, so it is data here however well-typed it was at the sender. A payload
 * whose shape is unrecognised answers `false`, which costs one refetch — the
 * safe direction, since the alternative is silently dropping a colleague's
 * message.
 */
function holdsComment(qc: QueryClient, reviewId: ReviewId, comment: unknown): boolean {
  if (typeof comment !== "object" || comment === null) return false
  const id: unknown = (comment as { id?: unknown }).id
  if (typeof id !== "number") return false

  const cached = qc.getQueryData<ConversationData>(qk.reviews.comments(reviewId))
  if (!cached) return false
  return cached.messages.some((m) => m.id === String(id))
}

/**
 * Subscribes a callback to invalidation frames for the given review.
 * Frames are debounced so a burst of `review.updated` events (a batch
 * edit from a colleague) collapses to a single refetch on the consumer.
 */
export function useLiveInvalidation(reviewId: ReviewId | null, onInvalidate: () => void): void {
  const callbackRef = React.useRef(onInvalidate)
  React.useEffect(() => {
    callbackRef.current = onInvalidate
  }, [onInvalidate])

  React.useEffect(() => {
    if (!reviewId) return
    const connection = connectReview(reviewId)
    let timer: ReturnType<typeof setTimeout> | null = null
    const off = connection.onFrame((frame) => {
      if (frame.type !== "review.updated" && frame.type !== "comment.created") {
        return
      }
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        timer = null
        callbackRef.current()
      }, INVALIDATION_DEBOUNCE_MS)
    })
    return () => {
      if (timer) clearTimeout(timer)
      off()
      connection.close()
    }
  }, [reviewId])
}
